import { useEffect } from 'react'

import { isTypingTarget } from '../lib/keys'

/**
 * Klavye kisayollari.
 *
 * ## Metin alanlarinda BAGLAMA DUYARLI devre disi
 *
 * `isTypingTarget` korumasi her kisayolda uygulaniyor: `Ctrl+K` bir textarea
 * icinde de calisabilir (modifier'li), ama `↑` calismamali - kullanici
 * mesajinin icinde satir gezerken son mesaji duzenleme moduna girmek
 * cildirtici olurdu.
 *
 * Bu yuzden kisayollar IKI gruba ayriliyor:
 *   * **Modifier'li** (`Ctrl/⌘ + …`): her yerde calisiyor. Kullanici
 *     modifier'a bastiysa niyeti acik.
 *   * **Ciplak** (`↑`, `Esc`): yalnizca metin alani DISINDA.
 *
 * ## `metaKey` ve `ctrlKey` ikisi de
 *
 * macOS'ta `⌘`, Windows/Linux'ta `Ctrl`. Yalnizca birini kontrol etmek,
 * kullanicinin yarisinda kisayollarin hic calismamasi demek.
 */

export interface Shortcuts {
  /** `Ctrl/⌘ + K` - komut paleti. */
  onPalette?(): void
  /** `Ctrl/⌘ + Shift + O` - yeni sohbet. */
  onNewChat?(): void
  /** `Ctrl/⌘ + B` - sidebar. */
  onToggleSidebar?(): void
  /** `Ctrl/⌘ + /` - kisayol yardimi. */
  onHelp?(): void
  /** `Esc` - modal kapat / uretimi durdur. */
  onEscape?(): void
}

export function useShortcuts(shortcuts: Shortcuts): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.ctrlKey || event.metaKey

      if (mod && event.shiftKey && event.key.toLowerCase() === 'o') {
        event.preventDefault()
        shortcuts.onNewChat?.()
        return
      }
      if (mod && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        shortcuts.onPalette?.()
        return
      }
      if (mod && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        shortcuts.onToggleSidebar?.()
        return
      }
      if (mod && event.key === '/') {
        event.preventDefault()
        shortcuts.onHelp?.()
        return
      }

      // CIPLAK kisayollar: yalnizca metin alani DISINDA.
      if (isTypingTarget(event.target)) return

      if (event.key === 'Escape') {
        shortcuts.onEscape?.()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [shortcuts])
}

/**
 * Yardim modal'inda listelenecek kisayollar.
 *
 * `action` bir CEVIRI ANAHTARI, metin degil: bu liste modul
 * seviyesinde ve orada `t()` cagirmak, dil degistiginde guncellenmeyen
 * donmus metinler uretirdi. Ceviri render aninda yapiliyor.
 *
 * Tus kombinasyonlari CEVRILMIYOR: `Ctrl`, `Shift`, `Enter` tus
 * adlari ve klavyede oyle yaziyor.
 */
export const SHORTCUT_HELP: { keys: string; action: string }[] = [
  { keys: 'Enter', action: 'shortcuts.send' },
  { keys: 'Shift + Enter', action: 'shortcuts.newline' },
  { keys: 'Ctrl/⌘ + K', action: 'shortcuts.search' },
  { keys: 'Ctrl/⌘ + Shift + O', action: 'shortcuts.newChat' },
  { keys: 'Ctrl/⌘ + B', action: 'shortcuts.toggleSidebar' },
  { keys: 'Ctrl/⌘ + /', action: 'shortcuts.help' },
  { keys: 'Esc', action: 'shortcuts.close' },
]
